import { useEffect } from 'react';

/**
 * Lightweight SEO hook — updates <title>, meta description, keywords, OG
 * tags and canonical on a per-route basis. No external dependency.
 * Restores previous values on unmount so tag-less pages don't leak stale
 * descriptions from the last visited route.
 */
const setMetaByName = (name, content) => {
  if (!content) return null;
  let el = document.querySelector(`meta[name="${name}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  const prev = el.getAttribute('content') || '';
  el.setAttribute('content', content);
  return { el, prev, created };
};

const setMetaByProp = (prop, content) => {
  if (!content) return null;
  let el = document.querySelector(`meta[property="${prop}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', prop);
    document.head.appendChild(el);
  }
  const prev = el.getAttribute('content') || '';
  el.setAttribute('content', content);
  return { el, prev, created };
};

const setCanonical = (href) => {
  if (!href) return null;
  let el = document.querySelector('link[rel="canonical"]');
  const created = !el;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  const prev = el.getAttribute('href') || '';
  el.setAttribute('href', href);
  return { el, prev, created };
};

export const useSEO = ({ title, description, keywords, ogTitle, ogDescription, canonical, ogImage }) => {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const refs = [
      description && setMetaByName('description', description),
      keywords && setMetaByName('keywords', keywords),
      (ogTitle || title) && setMetaByProp('og:title', ogTitle || title),
      (ogDescription || description) && setMetaByProp('og:description', ogDescription || description),
      canonical && setMetaByProp('og:url', canonical),
      canonical && setCanonical(canonical),
      ogImage && setMetaByProp('og:image', ogImage),
      setMetaByProp('og:type', 'website'),
      setMetaByName('twitter:card', ogImage ? 'summary_large_image' : 'summary'),
      (ogTitle || title) && setMetaByName('twitter:title', ogTitle || title),
      (ogDescription || description) && setMetaByName('twitter:description', ogDescription || description),
    ].filter(Boolean);

    return () => {
      document.title = prevTitle;
      refs.forEach((r) => {
        if (!r) return;
        if (r.created) { r.el.remove(); } else { r.el.setAttribute(r.el.tagName === 'LINK' ? 'href' : 'content', r.prev); }
      });
    };
  }, [title, description, keywords, ogTitle, ogDescription, canonical, ogImage]);
};

export default useSEO;
